import { addOnTypeOptions } from 'bk-web-app';
import {
  Button,
  SelectHookForm,
  SidebarSection,
  Text,
  TextareaHookForm,
  TextInputHookForm,
} from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import type { Addon } from '../types';
import { DateFormGroup, FormGroup } from '../FormGroups';
import { useClient } from '../../ClientContext';
import {
  collectionMethodOptions,
  dayOptions,
  monthOptions,
  optimizationOptions,
  thirdPartyLoginOptions,
  volumeOptions,
  yearOptions,
} from './formOptions';

const AddonSidebarFormLayout = styled.form`
  bottom: 0;
  display: grid;
  grid-template-rows: 1fr min-content;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const FormFields = styled.div`
  overflow: auto;
`;

const FormActions = styled.div`
  background-color: ${colors.pearl};
  column-gap: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 8px 24px;
`;

type AddonSidebarFormProps = {
  addon?: Addon;
  closeSidebar: () => void;
  isSubmitting: boolean;
  onSubmit: (data: unknown) => unknown;
  stripeId?: string;
  squareMerchantId?: string;
};

const AddonSidebarForm = ({
  addon,
  closeSidebar,
  isSubmitting,
  onSubmit,
  stripeId,
  squareMerchantId,
}: AddonSidebarFormProps) => {
  const client = useClient();
  const methods = useForm();
  const { handleSubmit, watch } = methods;
  const currentMonth = `0${new Date().getMonth() + 1}`.slice(-2).toString();
  const currentYear = new Date().getFullYear().toString();

  const dateInactiveMonth = watch('dateInactiveMonth', undefined);
  const dateInactiveYear = watch('dateInactiveYear', undefined);

  const isEditing = addon?.id;

  const createDateString = (year, month) => {
    if (year && month) {
      return `${year}-${month}`;
    }
    return null;
  };

  const createPayload = fieldValues => ({
    name: fieldValues?.name || null,
    order: addon?.order || 0,
    accountType: fieldValues?.accountType ? fieldValues?.accountType?.value : null,
    closeDayOfMonth: fieldValues?.closeDayOfMonth ? fieldValues?.closeDayOfMonth?.value : null,
    details: fieldValues?.details || null,
    thirdPartyLoginAvailable: fieldValues?.thirdPartyLoginAvailable
      ? fieldValues?.thirdPartyLoginAvailable?.value
      : null,
    optimizationStatus: fieldValues?.optimizationStatus
      ? fieldValues?.optimizationStatus?.value
      : null,
    collectionMethod: fieldValues?.collectionMethod ? fieldValues?.collectionMethod?.value : null,
    dateActive:
      fieldValues?.dateActiveYear && fieldValues?.dateActiveMonth
        ? createDateString(fieldValues?.dateActiveYear?.value, fieldValues?.dateActiveMonth?.value)
        : null,
    dateInactive:
      fieldValues?.dateInactiveYear && fieldValues?.dateInactiveMonth
        ? createDateString(
            fieldValues?.dateInactiveYear?.value,
            fieldValues?.dateInactiveMonth?.value,
          )
        : null,
    volume: fieldValues?.volume ? fieldValues?.volume?.value : null,
    clientId: client?.id,
    stripeId: stripeId || null,
    squareMerchantId: squareMerchantId || null,
  });

  return (
    <FormProvider {...methods}>
      <AddonSidebarFormLayout
        onSubmit={handleSubmit(fieldValues => onSubmit(createPayload(fieldValues)))}
      >
        <FormFields>
          <SidebarSection>
            <FormGroup>
              <Text color={colors.cloud} styleType="small" uppercase>
                Name
              </Text>
              <TextInputHookForm
                aria-label="Name"
                defaultValue={addon?.name}
                disabled={isSubmitting}
                name="name"
                rules={{ required: { value: true, message: 'Name is required' } }}
                type="text"
              />
              <Text color={colors.cloud} styleType="small" uppercase>
                Type
              </Text>
              <SelectHookForm
                isDisabled={isSubmitting}
                options={addOnTypeOptions}
                aria-label="Account Type"
                name="accountType"
                defaultValue={addon?.accountType || 'MERCHANT_PROCESSOR'}
                rules={{ required: { value: true, message: 'Type is required' } }}
                isSearchable
              />
            </FormGroup>
          </SidebarSection>
          <SidebarSection title="Notes" background={colors.shell}>
            <TextareaHookForm
              aria-label="Notes"
              defaultValue={addon?.details}
              disabled={isSubmitting}
              name="details"
              rules={{ maxLength: { value: 1000, message: 'Notes cannot exceed 1000 characters' } }}
              type="text"
            />
          </SidebarSection>
          <SidebarSection>
            <FormGroup>
              <Text color={colors.cloud} styleType="small" uppercase>
                Close Day
              </Text>
              <SelectHookForm
                isDisabled={isSubmitting}
                options={dayOptions}
                aria-label="Close Day"
                name="closeDayOfMonth"
                isClearable
                defaultValue={addon?.closeDayOfMonth}
                isSearchable
              />
              <Text color={colors.cloud} styleType="small" uppercase>
                Collection Method
              </Text>
              <SelectHookForm
                isDisabled={isSubmitting}
                options={collectionMethodOptions}
                aria-label="Collection Method"
                name="collectionMethod"
                defaultValue={addon?.collectionMethod}
                isSearchable
                rules={{
                  required: { value: true, message: 'Collection method is required' },
                }}
              />
              <Text color={colors.cloud} styleType="small" uppercase>
                Optimization
              </Text>
              <SelectHookForm
                isDisabled={isSubmitting}
                options={optimizationOptions}
                aria-label="Optimization Status"
                name="optimizationStatus"
                defaultValue={addon?.optimizationStatus}
                isSearchable
                isClearable
              />
              <Text color={colors.cloud} styleType="small" uppercase>
                Third Party Login
              </Text>
              <SelectHookForm
                isDisabled={isSubmitting}
                options={thirdPartyLoginOptions}
                aria-label="Third Party Login"
                name="thirdPartyLoginAvailable"
                defaultValue={addon ? addon.thirdPartyLoginAvailable : undefined}
              />
              <Text color={colors.cloud} required styleType="small" uppercase>
                Volume
              </Text>
              <SelectHookForm
                isDisabled={isSubmitting}
                options={volumeOptions}
                aria-label="Volume"
                name="volume"
                defaultValue={addon?.volume || 'LOW'}
                isClearable
              />
            </FormGroup>
          </SidebarSection>
          <SidebarSection background={colors.shell}>
            <DateFormGroup>
              <Text color={colors.cloud} styleType="small" uppercase>
                Date Active
              </Text>
              <SelectHookForm
                aria-label="Date Active Month"
                name="dateActiveMonth"
                isDisabled={isSubmitting}
                defaultValue={
                  // $FlowFixMe - Flow does not yet support method or property calls in optional chains.
                  isEditing ? addon?.dateActive?.substring(5) : currentMonth
                }
                options={monthOptions}
                rules={{
                  required: { value: true, message: 'Required field' },
                }}
              />
              <SelectHookForm
                aria-label="Date Active Year"
                name="dateActiveYear"
                isDisabled={isSubmitting}
                defaultValue={
                  // $FlowFixMe - Flow does not yet support method or property calls in optional chains.
                  isEditing ? addon?.dateActive?.substring(4, 0) : currentYear
                }
                rules={{ required: { value: true, message: 'Required field' } }}
                options={yearOptions}
              />
              <Text color={colors.cloud} styleType="small" uppercase>
                Date Inactive
              </Text>
              <SelectHookForm
                aria-label="Date Inactive Month"
                name="dateInactiveMonth"
                isDisabled={isSubmitting}
                isClearable
                // $FlowFixMe - Flow does not yet support method or property calls in optional chains.
                defaultValue={addon?.dateInactive?.substring(5)}
                options={monthOptions}
                rules={{
                  // invalid to provide inactive year but not inactive month
                  validate: value => (!value && dateInactiveYear?.value ? '' : true),
                }}
              />
              <SelectHookForm
                aria-label="Date Inactive Year"
                name="dateInactiveYear"
                isClearable
                isDisabled={isSubmitting}
                // $FlowFixMe - Flow does not yet support method or property calls in optional chains.
                defaultValue={addon?.dateInactive?.substring(4, 0)}
                options={yearOptions}
                rules={{
                  // invalid to provide inactive month but not inactive year
                  validate: value => (!value && dateInactiveMonth?.value ? '' : true),
                }}
              />
            </DateFormGroup>
          </SidebarSection>
        </FormFields>
        <FormActions>
          <Button
            disabled={isSubmitting}
            size="large"
            styleType="ghost"
            type="button"
            onClick={closeSidebar}
          >
            Cancel
          </Button>
          <Button
            aria-label="Save"
            disabled={isSubmitting}
            size="large"
            styleType="primary"
            type="submit"
          >
            Next
          </Button>
        </FormActions>
      </AddonSidebarFormLayout>
    </FormProvider>
  );
};

export default AddonSidebarForm;
